/**
 * @file    useDrawerAdapters.ts
 * @scope   SHARED
 * @summary Builds the thin adapters the drawer effects/handlers need: each one injects the
 *          store-owned context into a pure `common/utils` function, plus the two Guardrails
 *          payload appliers (chart-types catalog and chart Config).
 * @consumes common/utils (aliasResolver, dataSourceResolver, endpointBuilder, widgetBuilder,
 *           chartOptionsBuilder) and the domain hooks' setters
 * @produces A stable, memoized adapter bag consumed by the drawer effect hooks.
 * @sideEffects Only those owned by the injected setters (rule name, chart types, trend options…).
 * @notes WHY a hook and not inline consts: the effect hooks receive these adapters as arguments,
 *        so they must be initialized before the hook call. Keeping them here also keeps the
 *        orchestrator readable and every adapter memoized.
 */
import { useCallback } from 'react';
import { DIYChartOption } from 'constants/chart-selector';
import { normalizeGuardRailsConfig } from 'utils/normalizeGuardRailsConfig';
import { Rules } from '../types';
import { chartsWithTrend } from '../constants/chartCapabilities';
import {
  getOptionsByAlias,
  hasAliasChanged,
  resolveWidgetAlias,
  resolveWidgetByChartType,
} from '../utils/aliasResolver';
import { resolvePreviewDataSource, resolveSourceType } from '../utils/dataSourceResolver';
import { buildGetDataUrl } from '../utils/endpointBuilder';
import { buildDefaultOptions } from '../utils/widgetBuilder';
import { buildChartOptions, resolveInitialChartOption } from '../utils/chartOptionsBuilder';
import { EnterpriseChartTypeLike } from './useCurrentAlias';

/** Inputs of {@link useDrawerAdapters}. */
export type UseDrawerAdaptersParams = {
  /** Widget draft being edited. */
  customWidget: any;
  /** Widget catalogue. */
  widgetsList: any[];
  /** Data-source type used to resolve which widget backs a chart type. */
  dataSourceType: any;
  /** Dashboard view, used to resolve generic (client-side) data sources. */
  dashboardView: any;
  /** Environment endpoints. */
  endpoints: any;
  /** Child-chart selection. */
  selectedDiyChildChart: any;
  /** Chart option currently selected. */
  currentAlias: DIYChartOption | undefined;
  /** Trend currently selected. */
  selectedChartTrend: any;
  /** ENTERPRISE-ONLY: `true` when the selected chart belongs to the Enterprise family. */
  isDiyWidgetEngine: boolean;
  /** ENTERPRISE-ONLY: chart type the Enterprise widget renders through. */
  enterpriseChartType: EnterpriseChartTypeLike | undefined;
  /** Global store writer. */
  setState: (partial: any) => void;
  /** Publishes the child-chart rule name resolved while building the preview data source. */
  setRuleName: (ruleName: string) => void;
  /** Selects a chart option. */
  setCurrentAlias: (alias: DIYChartOption | undefined) => void;
  /** Replaces the dropdown options. */
  setChartTypes: (options: DIYChartOption[]) => void;
  /** Rule filter owned by `useChartRules`. */
  applyRuleFilter: (rules: Rules[], allowed: string[]) => void;
  /** Stores the raw rule set returned by Guardrails. */
  setResponseRules: (rules: any[]) => void;
  /** Refreshes the question/calculation pickers. */
  parseQuestionsAndCalculations: (config: any) => void;
  /** Refreshes the trend options. */
  parseTrendOptions: (config: any) => void;
  /** Clears the trend options for charts that do not support trend. */
  clearTrendOptions: () => void;
  /** Refreshes the filter catalogue. */
  parseChartFilters: (config: any) => void;
  /** Refreshes the data-collection catalogue. */
  parseQueryCollections: (config: any) => void;
  /**
   * Injected from the engine seam: `true` for options that must not be offered (gated-off
   * engine subtypes). Omitted → every option is kept.
   */
  isOptionHidden?: (optionValue?: string) => boolean;
  /**
   * Injected from the engine seam: component an engine subtype renders through, used when the
   * widget catalogue has no entry for its chart type. Omitted → no fallback.
   */
  resolveEngineComponent?: (optionValue?: string) => string | undefined;
  /**
   * Injected from the engine seam: `true` when the engine subtype for a chart declares a trend
   * (`hostLayout.showTrend`). Omitted → only the legacy trend charts parse trend options.
   */
  hasEngineTrend?: (chart?: string) => boolean;
};

/** The adapter bag consumed by the drawer effect hooks and handlers. */
export type DrawerAdapters = {
  currentOptionsAlias: (alias?: string) => string | undefined;
  filterRules: (rules: Rules[]) => void;
  setSourceType: (alias: string) => string;
  defaultOptions: (selectedWidget: any, currentWidget: any) => any;
  checkAlias: (chartType: string, alias: string) => boolean;
  getDataSourceForPreview: (newAlias: string, trend?: any) => any;
  getDataUrlEndpoint: (alias?: any) => string;
  getAlias: () => any;
  initializeChartTypes: (types: any) => void;
  applyChartConfig: (config: any, withFilters: boolean) => void;
};

export const useDrawerAdapters = ({
  customWidget,
  widgetsList,
  dataSourceType,
  dashboardView,
  endpoints,
  selectedDiyChildChart,
  currentAlias,
  selectedChartTrend,
  isDiyWidgetEngine,
  enterpriseChartType,
  setState,
  setRuleName,
  setCurrentAlias,
  setChartTypes,
  applyRuleFilter,
  setResponseRules,
  parseQuestionsAndCalculations,
  parseTrendOptions,
  clearTrendOptions,
  parseChartFilters,
  parseQueryCollections,
  isOptionHidden,
  resolveEngineComponent,
  hasEngineTrend,
}: UseDrawerAdaptersParams): DrawerAdapters => {
  /** Options lookup key for the current selection (alias, or business rule for child charts). */
  const currentOptionsAlias = useCallback(
    (alias?: string) => getOptionsByAlias(alias as string, selectedDiyChildChart),
    [selectedDiyChildChart]
  );

  /**
   * Injects store-owned collections into the pure widget resolver, falling back to the engine
   * subtype component when the catalogue does not map the chart type.
   */
  const getWidgetByChartType = useCallback(
    (type: DIYChartOption) => {
      const widget = resolveWidgetByChartType(type, widgetsList, dataSourceType);
      if (widget) return widget;
      const component = resolveEngineComponent?.(type.chartType);
      return component
        ? { alias: component, controllerName: type.chartType, rules: type.rules }
        : undefined;
    },
    [widgetsList, dataSourceType, resolveEngineComponent]
  );

  /** Injects the rule names accepted by the currently selected chart option. */
  const filterRules = useCallback(
    (rules: Rules[]) => applyRuleFilter(rules, currentAlias?.rules ?? []),
    [applyRuleFilter, currentAlias]
  );

  /** Injects the dashboard view into the pure source-type resolver. */
  const setSourceType = useCallback(
    (alias: string) => resolveSourceType(alias, dashboardView),
    [dashboardView]
  );

  /** Resolves the options lookup key before delegating to the pure builder. */
  const defaultOptions = useCallback(
    (selectedWidget: any, currentWidget: any) =>
      buildDefaultOptions(selectedWidget, currentWidget, currentOptionsAlias(selectedWidget.alias)),
    [currentOptionsAlias]
  );

  /** Feeds the pure comparator with the widget config and the selected option. */
  const checkAlias = useCallback(
    (chartType: string, alias: string) =>
      hasAliasChanged(
        chartType,
        alias,
        customWidget.customWidgetConfig?.rules || [],
        currentAlias?.rules || []
      ),
    [customWidget.customWidgetConfig, currentAlias]
  );

  /**
   * Adapter over `resolvePreviewDataSource`.
   * WHY: the pure resolver returns the child-chart rule name instead of mutating state, so the
   * `setRuleName` side effect is committed here — same behaviour, isolated ownership.
   */
  const getDataSourceForPreview = useCallback(
    (newAlias: string, trend?: any) => {
      const { dataSource, ruleName } = resolvePreviewDataSource(
        newAlias,
        selectedDiyChildChart,
        selectedChartTrend,
        trend
      );
      if (ruleName !== undefined) setRuleName(ruleName);
      return dataSource;
    },
    [selectedDiyChildChart, selectedChartTrend, setRuleName]
  );

  /** Supplies the environment template plus the child-chart and Medusa flags. */
  const getDataUrlEndpoint = useCallback(
    (alias?: any) =>
      buildGetDataUrl({
        getDataTemplate: endpoints.getData,
        currentAlias: alias,
        isChildChart: Boolean(selectedDiyChildChart?.isChild),
        hasMedusaQuery: Boolean(customWidget.hasMedusaQuery),
      }),
    [endpoints.getData, selectedDiyChildChart, customWidget.hasMedusaQuery]
  );

  /** Resolves the alias persisted in `customWidgetConfig.chart`. */
  const getAlias = useCallback(
    () => resolveWidgetAlias(currentAlias, customWidget),
    [currentAlias, customWidget]
  );

  /**
   * Turns a Guardrails chart-types response into the dropdown options and preselects the option
   * matching the widget being edited.
   */
  const initializeChartTypes = useCallback(
    (types: any) => {
      const chartOptions = buildChartOptions(types, getWidgetByChartType)
        // WHY: engine subtypes resolved through the fallback (e.g. CPR) are Medusa-backed, but the
        // catalogue only flags `hasMedusaQuery` on DataGrid children. Without it GetData goes to
        // the legacy branch and `dataCollectionsList` comes back empty (docs/guardrails.md).
        .map((option) =>
          !option.isChild && resolveEngineComponent?.(option.chartType)
            ? { ...option, hasMedusaQuery: true }
            : option
        )
        .filter((option) => !isOptionHidden?.(option.value as string | undefined));
      const { option, shouldPublishChildChart } = resolveInitialChartOption({
        options: chartOptions,
        customWidget,
        isDiyWidgetEngine,
        enterpriseOptionValue: enterpriseChartType?.optionValue,
      });

      // WHY: `selectedDiyChildChart` is only published for saved widgets; brand-new and TextBlock
      // widgets deliberately leave it untouched, as the original implementation did.
      if (shouldPublishChildChart) setState({ selectedDiyChildChart: option });
      setCurrentAlias(option);
      setChartTypes(chartOptions);
    },
    [
      getWidgetByChartType,
      isOptionHidden,
      resolveEngineComponent,
      customWidget,
      isDiyWidgetEngine,
      enterpriseChartType,
      setState,
      setCurrentAlias,
      setChartTypes,
    ]
  );

  /**
   * Applies a Guardrails chart `Config`: pickers, trend options, collections and rules.
   *
   * WHY trend availability is decided here (and not inside `useChartTrend`): it depends on the
   * chart type reported by the same payload.
   *
   * @param withFilters `true` only for the chart-type-change channel, which also refreshes the
   *                    filter catalogue.
   */
  const applyChartConfig = useCallback(
    (rawConfig: any, withFilters: boolean) => {
      const config = normalizeGuardRailsConfig(rawConfig);
      parseQuestionsAndCalculations(config);

      // WHY `hasEngineTrend`: engine subtypes (e.g. CPR) declare their trend in the descriptor
      // instead of being listed in `chartsWithTrend`.
      if (
        chartsWithTrend.includes(config?.chart) ||
        config?.chart === 'DataGrid' ||
        hasEngineTrend?.(config?.chart)
      )
        parseTrendOptions(config);
      else clearTrendOptions();

      if (withFilters) parseChartFilters(config);

      parseQueryCollections(config);
      setResponseRules(config?.rules || []);
      filterRules(config?.rules || []);
    },
    [
      parseQuestionsAndCalculations,
      parseTrendOptions,
      clearTrendOptions,
      hasEngineTrend,
      parseChartFilters,
      parseQueryCollections,
      setResponseRules,
      filterRules,
    ]
  );

  return {
    currentOptionsAlias,
    filterRules,
    setSourceType,
    defaultOptions,
    checkAlias,
    getDataSourceForPreview,
    getDataUrlEndpoint,
    getAlias,
    initializeChartTypes,
    applyChartConfig,
  };
};
